import React, { Suspense, useEffect, useState } from 'react';
import Card from '@/components/card';
import Title from '@/components/title';
import { IDataModal } from '@/interfaces/dataModal';
import {
  IProduct,
  ISearchProductReq,
  ISearchProductRes,
} from '@/interfaces/product';
import { Button, Input, Radio, TablePaginationConfig } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { searchProducts } from '@/adapters/product';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import ProductTable from './components/product-table';
import ProductAddModal from './components/product-add-modal';
import { isFullAccess } from '@/utils/permissionUtils';
import { getPathSegment } from '@/utils/pathUtils';
import { ProductMenu } from '@/configs/menuItems';
import SubMenu from '@/components/menu/sub-menu';
const _productModal: IDataModal<IProduct> = {
  open: false,
};

const { Search } = Input;

const _criteria: ISearchProductReq = {
  orderBy: 'id',
  orderType: '',
  pageSize: 10,
  pageStart: 1,
  search: '',
  isDelete: false,
};

const Product = () => {
  const pathSegment = getPathSegment();
  const perFullAccess = isFullAccess();
  const [criteria, setCriteria] = useState<ISearchProductReq>({
    ..._criteria,
  });
  const [product, setProduct] = useState<ISearchProductRes>({
    data: [],
    totalRecords: 0,
  });
  const [dataModal, setDataModal] =
    useState<IDataModal<IProduct>>(_productModal);

  const mutations = useMutation({
    mutationFn: async () => await searchProducts(criteria),
    mutationKey: ['fetchProduct'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        setProduct(data.data);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const fetchProduct = async () => {
    try {
      mutations.mutateAsync();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (criteria) {
      fetchProduct();
    }
  }, [criteria]);
  const onActionProduct = () => {
    setDataModal({ ..._productModal });
    fetchProduct();
  };

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IProduct> | SorterResult<IProduct>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IProduct) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onChange = (value: string) => {
    let iSample: boolean | undefined = false;
    let isEquipment: boolean | undefined = false;
    switch (value) {
      case 'product':
        iSample = false;
        isEquipment = false;
        break;
      case 'sample':
        iSample = true;
        isEquipment = false;
        break;
      case 'equipment':
        iSample = true;
        isEquipment = true;
        break;
      default:
        break;
    }

    setCriteria({
      ...criteria,
      search: '',
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
      iSample,
      isEquipment,
    });
  };

  return (
    <>
      <SubMenu active={pathSegment} items={ProductMenu} />
      <Card>
        <Title title="จัดการสินค้า">
          <div className="flex flex-row gap-2">
            {perFullAccess && (
              <Button
                type="primary"
                className="max-w-[100px]"
                onClick={() => {
                  setDataModal({
                    ...dataModal,
                    open: true,
                  });
                }}
              >
                <PlusOutlined /> เพิ่มใหม่
              </Button>
            )}
          </div>
        </Title>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <div className="flex flex-col md:flex-row justify-between gap-3">
              <Radio.Group
                size="large"
                onChange={e => onChange(e.target.value)}
                buttonStyle="solid"
                defaultValue={'product'}
              >
                <Radio.Button value={'product'}>
                  <div className="flex items-center gap-1">สินค้า</div>
                </Radio.Button>
                <Radio.Button value={'sample'}>
                  <div className="flex items-center gap-1">ตัวอย่าง</div>
                </Radio.Button>
                <Radio.Button value={'equipment'}>
                  <div className="flex items-center gap-1">อุปกรณ์</div>
                </Radio.Button>
              </Radio.Group>
              <Search
                className="md:max-w-[400px] md:min-w-[400px]"
                placeholder="ค้นหา"
                allowClear
                size="large"
                enterButton="ค้นหา"
                onSearch={onSearch}
              />
            </div>
            <div className="flex justify-end gap-3"></div>
          </div>
          <ProductTable
            criteria={criteria}
            product={product}
            loading={mutations.isPending}
            onEditData={item =>
              setDataModal({ ..._productModal, id: item.id, item, open: true })
            }
            onDelete={onActionProduct}
            onTableChange={onTableChange}
          />
        </div>
        <Suspense>
          <ProductAddModal
            handleCancel={() => setDataModal({ ..._productModal })}
            handleOk={() => onActionProduct()}
            dataModal={dataModal}
          />
        </Suspense>
      </Card>
    </>
  );
};

export default Product;
